/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { dataEqual } from "@adapptio/lib-blueprint";

import {
	createEventEmitter,
	emitEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";

import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { ViewService } from "../view/ViewService";
import { DialogService } from "../dialogs/DialogService";
import { IErrorGroup, IErrorListData, TErrorsSeverityCount } from "../errors/ErrorsTypes";
import { getEmptySeverityCount, processEditorApiErrorList } from "../errors/errorsUtils";
import { IEditorApiDeployEnvironmentListResponse, IEditorApiDeploymentListResponse } from "@adapptio/lib-core";
import { ScmService } from "../scm/ScmService";

const ERRORS_RELOAD_INTERVAL = 60000;
const DEPLOYMENT_LIST_RELOAD_INTERVAL = 120000;
const DEPLOYMENT_ENVIRONMENT_LIST_RELOAD_INTERVAL = 300000;

/**
 * Service to manage deployments
 */
export class DeploymentService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** API Service Instance */
	private apiService: IEditorApiService;

	/** View Service Instance */
	private viewService: ViewService;

	/** Dialog Service Instance */
	private dialogService: DialogService;

	private scmService: ScmService;

	/* Reload timers */
	private releaseListReloadTimer: ReturnType<typeof setTimeout>;
	private deploymentListReloadTimer: ReturnType<typeof setTimeout>;
	private deploymentEnvironmentListReloadTimer: ReturnType<typeof setTimeout>;
	private errorsReloadTimer: ReturnType<typeof setInterval>;

	/** List of deployments */
	private deploymentListData: IEditorApiDeploymentListResponse = { deployments: [] };

	/** List of available deployment environments */
	private deploymentEnvironmentListData: IEditorApiDeployEnvironmentListResponse = { environments: [] };

	/** Error list data */
	private errorListData: IErrorListData;

	/* Events */
	public onReleaseListChange = createEventEmitter<void>();
	public onDeploymentListChange = createEventEmitter<void>();
	public onDeploymentEnvironmentListChange = createEventEmitter<void>();
	public onErrorsChanged = createEventEmitter<void>();

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("DeploymentService");

		this.apiService = dc.get("apiService");
		this.dialogService = dc.get("dialogService");
		this.viewService = dc.get("viewService");
		this.scmService = dc.get("scmService");
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		this.handleDeploymentListReload(false);
		this.handleDeploymentEnvironmentListReload(false);

		this.startErrorsReloading();
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		if (this.releaseListReloadTimer) {
			clearTimeout(this.releaseListReloadTimer);
		}

		if (this.deploymentListReloadTimer) {
			clearTimeout(this.deploymentListReloadTimer);
		}

		if (this.deploymentEnvironmentListReloadTimer) {
			clearTimeout(this.deploymentEnvironmentListReloadTimer);
		}

		this.stopErrorsReloading();

		removeAllEventListeners(this.onReleaseListChange);
		removeAllEventListeners(this.onDeploymentListChange);
		removeAllEventListeners(this.onDeploymentEnvironmentListChange);
		removeAllEventListeners(this.onErrorsChanged);
	}

	private handleDeploymentListReload(loadStatus = true) {
		if (loadStatus) {
			this.loadDeploymentList();
		}

		this.deploymentListReloadTimer = setTimeout(
			() => this.handleDeploymentListReload(),
			DEPLOYMENT_LIST_RELOAD_INTERVAL
		);
	}

	private handleDeploymentEnvironmentListReload(loadStatus = true) {
		if (loadStatus) {
			this.loadDeploymentEnvironmentList();
		}

		this.deploymentEnvironmentListReloadTimer = setTimeout(
			() => this.handleDeploymentEnvironmentListReload(),
			DEPLOYMENT_ENVIRONMENT_LIST_RELOAD_INTERVAL
		);
	}

	private async loadDeploymentList(): Promise<void> {
		try {
			const data = await this.apiService.getDeploymentList();

			if (!dataEqual(data, this.deploymentListData)) {
				this.deploymentListData = data;
				emitEvent(this.onDeploymentListChange);
			}
		} catch (err) {
			this.logger.error("Failed to load deployment list:", err);
		}
	}

	private async loadDeploymentEnvironmentList(): Promise<void> {
		try {
			const data = await this.apiService.getDeploymentEnvironmentList();

			if (!dataEqual(data, this.deploymentEnvironmentListData)) {
				this.deploymentEnvironmentListData = data;
				emitEvent(this.onDeploymentEnvironmentListChange);
			}
		} catch (err) {
			this.logger.error("Failed to load deployment environment list:", err);
		}
	}

	public getErrorGroups(): IErrorGroup[] {
		return this.errorListData?.errorGroups?.size > 0
			? Array.from(this.errorListData.errorGroups.values())
			: [];
	}

	public getErrorsSeverityCount(): TErrorsSeverityCount {
		return (
			this.errorListData?.severityCount || {
				...getEmptySeverityCount()
			}
		);
	}

	private setErrorListData(errorListData: IErrorListData): void {
		this.errorListData = { ...errorListData };
		emitEvent(this.onErrorsChanged);
	}

	private async loadErrorsToCache(): Promise<void> {
		const errorList = await this.apiService.getErrors();
		const errorListData = processEditorApiErrorList(errorList);
		this.setErrorListData(errorListData);
	}

	private startErrorsReloading(): Promise<void> {
		if (this.errorsReloadTimer) {
			this.stopErrorsReloading();
		}

		this.errorsReloadTimer = setInterval(() => {
			this.loadErrorsToCache();
		}, ERRORS_RELOAD_INTERVAL);

		return this.loadErrorsToCache();
	}

	private stopErrorsReloading(): void {
		clearInterval(this.errorsReloadTimer);
		this.errorsReloadTimer = null;
	}

	public async loadErrors(): Promise<void> {
		if (this.errorsReloadTimer) {
			return this.startErrorsReloading();
		}

		return this.loadErrorsToCache();
	}

	public isDeployInProgress(): boolean {
		return this.deploymentListData.deployments.some(deployment => deployment.status === "in_progress");
	}

	/**
	 * Deploys a release to an environment
	 * @param envName Environment name
	 * @param tagName Release tag name
	 */
	public async deploy(
		envName: string,
		tagName: string
	): Promise<void> {
		const releaseList = this.scmService.getReleaseList();

		const tagExists = releaseList.releases.some((release => release.tagName === tagName));
		const environmentExists = this.deploymentEnvironmentListData?.environments?.some((env) => env.name === envName);

		if (!tagExists) {
			throw new Error(`Tag name "${tagName}" does not exist. Please choose a different name.`);
		}

		if (!environmentExists) {
			throw new Error(`Environment "${envName}" does not exist. Please choose a different environment.`);
		}

		await this.apiService.deploy(envName, tagName);
	}
}
